import { ReactNode, FC } from 'react'
import {
  Box,
  VStack,
  Text,
  useColorMode,
  Button,
  HStack,
  Image,
  Stack,
  StackProps,
  useBreakpointValue,
} from '@chakra-ui/react'
import DAppLayout from '../dApp/layouts/DAppLayout'
import { IconType } from 'react-icons'
import { PiWallet, PiUsers, PiSuitcase } from 'react-icons/pi'
import Link from 'next/link'
import { WalletMultiButton } from '../../components/wallet-ui'
import { FloatingButton } from './components/FloatingBadge'
import { LevelCard } from './components/RewardsHeaderDetails'
import NextLink from 'next/link'
import { useWallet } from '@solana/wallet-adapter-react'
import { HiOutlineTicket } from 'react-icons/hi'
import { minPointsForLevel } from './utils/levelUtils'
import { TwitterLinkMultiButton } from '../admin/components/twitter-link-ui/TwitterLinkMultiButton'
import { DiscordLinkMultiButton } from '../admin/components/discord-link-ui/DiscordLinkMultiButton'


export const DAppRewardsLanding = () => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const { publicKey: userPubKey } = useWallet()

  const RewardCard: FC<{
    Icon: IconType
    title: string
    description: string
    gradientDirection: string
    children: ReactNode
    style?: object
    className?: string
  }> = ({
    Icon,
    title,
    description,
    gradientDirection,
    children,
    style,
    className,
  }) => {
    return (
      <VStack
        bgGradient={`linear(${gradientDirection},  ${
          isDarkMode ? '#292929' : '#F8F8F8'
        },  #FFFFFF00)`}
        borderRadius='20'
        width={600}
        p={50}
        style={style}
        className={`${className}`}
        maxW='90vw'
      >
        <Icon size={40} color={isDarkMode ? 'white' : 'black'} />
        <Text fontSize={24} fontWeight={'semibold'}>
          {title}
        </Text>
        <Text color='#7B7B7B'>{description}</Text>
        {children}
      </VStack>
    )
  }

  const RaffleCard: FC<{
    image: string
    marginTop?: StackProps['marginTop']
  }> = ({ image, marginTop }) => {
    return (
      <VStack
        w='85%'
        border='solid 1px'
        borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
        bgColor={isDarkMode ? '#24272c' : 'white'}
        borderRadius='30px'
        gap='2'
        paddingBottom='4'
        marginTop={marginTop}
      >
        <HStack
          w='full'
          px='5'
          pt='2'
          justifyContent='space-between'
          alignItems='center'
        >
          <HStack>
            <Stack
              borderRadius='full'
              h='20px'
              w='20px'
              bgColor={isDarkMode ? 'white' : 'black'}
            ></Stack>
            <Text>Monet</Text>
          </HStack>
          <HStack
            py='1'
            px='4'
            border='1px solid'
            borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
            borderRadius='full'
          >
            <HiOutlineTicket size={20} color={isDarkMode ? 'white' : 'black'} />
            <Text>Raffle</Text>
          </HStack>
        </HStack>
        <Image src={image} marginTop='0px !important' />
        <HStack w='90%' justifyContent='space-between' alignItems='center'>
          <Text>DeadGod #1234</Text>
          <Image src='./images/rewards/badges/new.svg' />
        </HStack>
        <HStack w='90%' justifyContent='space-between' alignItems='center'>
          <VStack alignItems={'flex-start'}>
            <Text textAlign={'left'} fontSize={'20px'} fontWeight={'bold'}>
              5312 / 8000
            </Text>
            <Text textAlign={'left'} color={'#7B7B7B'}>
              Tickets sold
            </Text>
          </VStack>
          <HStack
            border='solid 1px'
            borderRadius='20px'
            borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
            gap='1'
            py='1'
            px='2'
          >
            <Text>13</Text>
            <Image src='/images/rewards/GemsShine.svg' />
          </HStack>
        </HStack>
        <HStack
          bgColor={isDarkMode ? 'white' : 'black'}
          w='90%'
          justifyContent='space-between'
          alignItems='center'
          px='4'
          py='2'
          borderRadius='full'
          mt='20px !important'
          mb='10px !important'
        >
          <Text color={isDarkMode ? 'black' : 'white'} fontWeight='bold'>
            You Entered with
          </Text>
          <HStack
            border='solid 1px'
            borderRadius='20px'
            borderColor={isDarkMode ? '#49494950' : '#E9E9E9'}
            gap='1'
            py='1'
            px='2'
          >
            <Text color={isDarkMode ? 'black' : 'white'} fontWeight='bold'>
              39
            </Text>
            <Image src='/images/rewards/GemsShine.svg' />
          </HStack>
        </HStack>
      </VStack>
    )
  }

  const RewardSection: FC<{
    reverse?: boolean
    color: string
    titleSmall: string
    titleBig: string
    description: ReactNode
    uri?: string
    table?: boolean
  }> = ({ titleSmall, titleBig, description, uri, reverse, color, table }) => {
    const smallDevice = useBreakpointValue({ base: true, lg: false })

    return (
      <Stack
        display={'flex'}
        justifyContent={['space-between']}
        alignItems={['center']}
        w={'100%'}
        gap={[6, 10, 20]}
        flexDirection={['column', 'column', 'row']}
      >
        {reverse && !smallDevice && <Image src={uri} />}
        <VStack
          justifyContent={'space-evenly'}
          alignItems={['center', 'flex-start']}
          maxWidth={'750px'}
        >
          <Text color={color} fontSize={20}>
            {titleSmall}
          </Text>
          <Text fontSize={44} fontWeight={'semibold'}>
            {titleBig}
          </Text>
          <Text color='#7B7B7B' fontSize={16}>
            {description}
          </Text>
        </VStack>
        {(!reverse || smallDevice) && (
          <Image src={uri} maxW={['full', 'full', '400px', 'full']} />
        )}
        {table && <LevelTable />}
      </Stack>
    )
  }

  const LevelTable = () => {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' w='500px'>
        <VStack
          border='solid 1px'
          minW='200px'
          width='200px'
          borderRadius='10px'
          paddingBottom='3'
          borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
        >
          <HStack
            w='full'
            justifyContent='space-between'
            mb='3'
            p='3'
            borderBottom='1px solid'
            borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
          >
            <Text fontWeight='bold'>Level</Text>
            <Text fontWeight='bold'>Points</Text>
          </HStack>
          {minPointsForLevel.map((item) => {
            return (
              <HStack w='full' justifyContent='space-between' px='3'>
                <Image src={`/images/rewards/levelBadges/${item.level}.svg`} />
                <Text>{item.points}</Text>
              </HStack>
            )
          })}
        </VStack>
      </Box>
    )
  }

  return (
    <DAppLayout>
      {/* <RewardsModal /> */}
      {/* <RewardsLevelUpModal /> */}

      <FloatingButton />
      <Box
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        flexDirection={'column'}
      >
        <VStack
          width='100%'
          position='relative'
          h={'630px'}
          paddingTop={'60px'}
          paddingBottom={'120px'}
          backgroundImage={'url("/images/rewards/rewardsBanner.png")'}
          borderRadius={60}
          backgroundRepeat='no-repeat'
          backgroundPosition='center'
        >
          <VStack
            display={'flex'}
            w={'full'}
            h={'100%'}
            justifyContent={'space-between'}
          >
            <Box
              border={'#444444 solid 1px'}
              zIndex={1}
              padding={'10px 40px 10px 40px'}
              borderRadius={100}
              fontSize={14}
            >
              <Text size={'14x'} color={'white'}>
                INTRODUCING 🌟
              </Text>
            </Box>

            <Text
              position={'relative'}
              zIndex={1}
              fontSize={[60, 90, 100]}
              fontWeight={'extrabold'}
              color={'white'}
              textAlign={'center'}
              lineHeight={['70px', '100px']}
            >
              <Image
                src='/images/rewards/bow.svg'
                position='absolute'
                bottom={['55px', '87px']}
                left={['-20px']}
                w={['60px', '80px']}
              />
              Monet
              <br />
              Rewards
            </Text>

            <Text
              zIndex={1}
              fontSize={16}
              color={'#BDBDBD'}
              w='80%'
              alignSelf='center'
              textAlign='center'
            >
              Enter raffles, collect points. Earn rewards. Repeat.
            </Text>
            <Link href='/rewardsCenter'>
              <Button
                zIndex={1}
                fontSize={'18px'}
                fontWeight={'bold'}
                variant={'primaryDark'}
              >
                Go to rewards center
              </Button>
            </Link>
          </VStack>
        </VStack>

        <VStack marginTop={'150px'} alignItems='center'>
          <Text fontSize={'56px'} fontWeight={'bold'} textAlign='center'>
            Raffles Just Got More Fun 🎊
          </Text>
          <Text color={'#7B7B7B'}>
            Monet rewards you for daily challenges and interactions.
          </Text>
          <Stack
            w='full'
            alignItems={['center']}
            flexDirection={['column', 'column', 'row']}
            gap='4'
          >
            <VStack
              borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
              bgColor={isDarkMode ? 'cardBlack' : '#EEEEEE'}
              w='400px'
              maxW={['90vw', '450px']}
              h='600px'
              borderRadius='30px'
              position='relative'
              overflow='hidden'
            >
              <Stack
                w='full'
                h='full'
                top='-105px'
                position='absolute'
                justifyContent='center'
                alignItems='center'
              >
                <RaffleCard image={'./images/rewards/degod.svg'} />

                <Text
                  color={isDarkMode ? 'white' : 'black'}
                  fontSize={'20px'}
                  fontWeight={'bold'}
                  marginTop='50px !important'
                >
                  Enter Raffles
                </Text>
              </Stack>
            </VStack>

            <Stack
              flexDirection={['column']}
              maxW={['450px', '450px', '450px', 'full']}
              gap='10px'
              mt={'0 !important'}
            >
              <Stack
                w='full'
                flexDirection={['column', 'column', 'column', 'row']}
                gap='4'
              >
                <VStack
                  w='full'
                  borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
                  bgColor={isDarkMode ? 'cardBlack' : '#EEEEEE'}
                  borderRadius='30px'
                  h='250px'
                  justifyContent='center'
                >
                  <LevelCard experiencePoints={10000} />
                  <HStack>
                    <LevelCard experiencePoints={1000} />
                    <LevelCard experiencePoints={5000} />

                    {/* <LevelCard experiencePoints={100} /> */}
                  </HStack>
                  <HStack>
                    <Box transform='rotate(-10deg)'>
                      <LevelCard experiencePoints={100} />
                    </Box>
                    <Box transform='rotate(10deg)'>
                      <LevelCard experiencePoints={500} />
                    </Box>
                  </HStack>
                  <Text
                    color={isDarkMode ? 'white' : 'black'}
                    fontSize={'20px'}
                    fontWeight={'bold'}
                  >
                    Levels
                  </Text>
                </VStack>
                <VStack
                  w='full'
                  borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
                  bgColor={isDarkMode ? 'cardBlack' : '#EEEEEE'}
                  borderRadius='30px'
                  h='250px'
                  justifyContent='center'
                  margin='0px !important'
                >
                  <HStack justifyContent='center'>
                    <Image
                      src='./images/rewards/badges/new.svg'
                      w='125px'
                      margin='-20px !important'
                    />
                    <Image
                      src='./images/rewards/badges/top.svg'
                      w='125px'
                      margin='-20px !important'
                      marginTop='20px !important'
                    />
                    <Image
                      src='./images/rewards/badges/dao.svg'
                      w='125px'
                      margin='-20px !important'
                    />
                  </HStack>
                  <Text
                    color={isDarkMode ? 'white' : 'black'}
                    fontSize={'20px'}
                    fontWeight={'bold'}
                  >
                    Badges
                  </Text>
                </VStack>
              </Stack>
              <VStack
                h='340px'
                w={['400px', '400px', '400px', '850px']}
                borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
                bgColor={isDarkMode ? 'cardBlack' : '#EEEEEE'}
                borderRadius='30px'
                position='relative'
                overflow='hidden'
              >
                <Text
                  color={isDarkMode ? 'white' : 'black'}
                  fontSize={'20px'}
                  fontWeight={'bold'}
                  mt='40px'
                >
                  Weekly Prizes and Rewards
                </Text>
                <HStack alignItems={'flex-start'}>
                  <RaffleCard image='./images/rewards/lad-sample.webp' />
                  <RaffleCard image='./images/rewards/sac-sample.webp' />
                  <RaffleCard image='./images/rewards/degod.svg' />
                </HStack>
                <Stack
                  position='absolute'
                  top='110px'
                  left='250px'
                  w='350px'
                ></Stack>

                <Stack
                  position='absolute'
                  top='120px'
                  left='480px'
                  w='350px'
                ></Stack>
              </VStack>
            </Stack>
          </Stack>
        </VStack>

        <VStack
          marginTop={['80px', '110px', '140px']}
          maxW={['80vw']}
          gap={['16', '30', '52']}
        >
          <RewardSection
            color={isDarkMode ? '#7B7B7B' : 'black'}
            titleSmall='Collect'
            titleBig='Collect points on every transaction'
            description={
              <Text color='#7B7B7B' fontSize={16}>
                Your loyalty pays off in more ways than one – with each
                transaction, you're one step closer to enjoying fantastic perks
                and surprises. 🌟💫 <br /> <br /> When you buy a ticket to a
                raffle, you will earn 10 Points and 100 Gems for every 1 SOL in
                value.
                <br /> <br /> When you host a raffle, you will earn 1 Point and
                10 Gems for every 1 SOL in volume.
              </Text>
            }
            uri='/images/rewards/collect.svg'
          />

          <RewardSection
            reverse={true}
            color={isDarkMode ? '#7B7B7B' : 'black'}
            // reverse={true}
            titleSmall='Levels'
            titleBig='Level UP as you go with badges 🏆 '
            description={
              <Text color='#7B7B7B' fontSize={16}>
                Watch your rewards soar to new heights! 🏆🌟 <br></br>Earn
                prestigious level badges as you achieve milestones, and unlock
                exclusive perks at each level.
                <br />
                <br />
                Special badges such as Raffle DAO, TOP Buyer & Raffler can also
                be earned and allows you to access additional benefits.
              </Text>
            }
            uri='/images/rewards/levels.svg'
          />

          <RewardSection
            color={isDarkMode ? '#7B7B7B' : 'black'}
            titleSmall='Redeem'
            titleBig='Enter exclusive raffles'
            description={
              <Text color='#7B7B7B' fontSize={16}>
                Enter exclusive raffles gated by badge and levels. Spend or keep
                your GEMS. Decide wisely.
              </Text>
            }
            uri='/images/rewards/redeem.svg'
          />
        </VStack>

        <VStack
          marginTop={'150px'}
          maxW={'90vw'}
          justifyContent='center'
          alignItems='center'
        >
          <Text
            color={isDarkMode ? 'white' : 'black'}
            fontWeight={'extrabold'}
            fontSize={[44, 54, 64]}
            textAlign='center'
          >
            Get Started on <br /> Monet Rewards
          </Text>
          <Text>Start collecting and earning in three simple steps.</Text>
          <Stack
            py={'50px !important'}
            flexDirection={['column', 'column', 'row']}
            justifyContent={'center'}
            alignItems='center'
            gap='4'
          >
            <RewardCard
              Icon={PiWallet}
              title='Connect Wallet'
              description='We recommend Phantom or Solflare'
              gradientDirection='to-b'
              style={{}}
              className={isDarkMode ? 'boxLight one' : 'box'}
            >
              <WalletMultiButton variant='normal' />
            </RewardCard>
            <RewardCard
              Icon={PiUsers}
              title='Get Social'
              description='Brag about your progress with friends'
              gradientDirection='to-b'
              className={isDarkMode ? 'boxLight two' : 'box'}
            >
              <HStack>
                <TwitterLinkMultiButton></TwitterLinkMultiButton>
                <DiscordLinkMultiButton></DiscordLinkMultiButton>
              </HStack>
            </RewardCard>
          </Stack>
          <RewardCard
            Icon={PiSuitcase}
            title='Redeem'
            description='Enter Raffles and Trade points'
            gradientDirection='to-t'
            className={isDarkMode ? 'boxLight three' : 'box'}
          >
            <NextLink href='rewardsCenter'>
              <Button variant={isDarkMode ? 'primaryDark' : 'primary'}>
                Go to Rewards Center
              </Button>
            </NextLink>
          </RewardCard>
        </VStack>
      </Box>
    </DAppLayout>
  )
}
